export type TaxonomyHead = {
  id: number
  name: string
  code?: string
  system?: string | null
}

export type TaxonomyWithColor = TaxonomyHead & {
  color: string
}

export interface Taxonomy {
  slug: string | null
  localizedName: string
  uri: string
  isEditable: boolean
}

type ArchiveTypeItem = TaxonomyHead & {
  type: string
}

type ApplicantOrderStatusItem = TaxonomyWithColor & {
  specialStatus: string | null
  isFinal: boolean
}

type AttachmentTypeItem = TaxonomyHead & {
  mandatory: boolean
}

type ContractTermTaxonomyItem = TaxonomyHead & {
  hasFixedDuration: boolean
}

export type ActionTypeItem = TaxonomyHead & {
  context?: 'applicant' | 'contact'
  expectsCompletion: boolean
  color?: string | null
  icon?: string | null
}

export type TaxonomyKeyMap = {
  applicant_order_statuses: ApplicantOrderStatusItem[]
  statuses: TaxonomyWithColor[]
  unavailability_types: TaxonomyHead[]
  attachment_types: AttachmentTypeItem[]
  segmentations: TaxonomyHead[]
  action_types: ActionTypeItem[]
  study_levels: TaxonomyHead[]
  experiences: TaxonomyHead[]
  contract_terms: ContractTermTaxonomyItem[]
  employee_statuses: TaxonomyHead[]
  languages: TaxonomyHead[]
  softwares: TaxonomyHead[]
  authorizations: TaxonomyHead[]
  reasons: TaxonomyHead[]
  invoice_types: TaxonomyHead[]
  vat_rates: TaxonomyHead[]
  cv_sources: TaxonomyHead[]
  roles: TaxonomyHead[]
  archive_types: ArchiveTypeItem[]
  nafs: TaxonomyHead[]
  qualifications: TaxonomyHead[]
  specialities: TaxonomyHead[]
  company_statuses: TaxonomyWithColor[]
  sectors: TaxonomyHead[]
  potentials: TaxonomyHead[]
}

export type TaxonomySlug = keyof TaxonomyKeyMap

export type TaxonomyItems<S extends TaxonomySlug> = TaxonomyKeyMap[S]

export type TaxonomyItem<S extends TaxonomySlug> = TaxonomyKeyMap[S][number]

export type QualificationWithParent = TaxonomyItem<'qualifications'> & {
  parent: {
    id: number
  } | null
  specialities?: TaxonomyItems<'specialities'>
}

export type SegmentationWithParent = TaxonomyItem<'segmentations'> & {
  parent: {
    id: number
  } | null
}

export const taxonomyQueryStaleTime = 1000 * 60 * 60 * 24

export const applicantStatusArchivedId = -1

export const languageLevels = [
  {
    id: 1,
    name: 'Maternelle'
  },
  {
    id: 2,
    name: 'Bilingue'
  },
  {
    id: 3,
    name: 'Courant'
  },
  {
    id: 4,
    name: 'Professionnel'
  },
  {
    id: 5,
    name: 'Scolaire'
  }
]

export const softwareLevels = [
  {
    id: 1,
    name: 'Notions'
  },
  {
    id: 2,
    name: 'Maîtrisé'
  },
  {
    id: 3,
    name: 'Intermédiaire'
  },
  {
    id: 4,
    name: 'Expert'
  }
]

export const mobilityTypes = [
  { id: 1, name: 'Géolocalisation' },
  { id: 2, name: 'Département' },
  { id: 3, name: 'Région' },
  { id: 4, name: 'Pays' }
]

export const currencyOptions = [
  { id: 1, name: '€' },
  { id: 2, name: 'K€', factor: 1000 }
]

export const buildRadiusOptions = (radius: 'basic' | 'extended' | 'full') => {
  const options = []
  options.push({ id: 2, name: '2 km' })
  options.push({ id: 5, name: '5 km' })
  for (let i = 10; i <= 100; i += 10) {
    options.push({ id: i, name: `${i} km` })
  }
  if (radius === 'extended' || radius === 'full') {
    for (let i = 120; i <= 280; i += 20) {
      options.push({ id: i, name: `${i} km` })
    }
  }
  if (radius === 'full') {
    for (let i = 300; i <= 550; i += 50) {
      options.push({ id: i, name: `${i} km` })
    }
  }
  return options
}

export type VatRate = TaxonomyItem<'vat_rates'> & {
  rate: number
}
